import { takeLatest, select, put } from "redux-saga/effects";
import { statusSuccess } from "@grandvisionhq/state";
import "../../../state/actions.js";
import { getBundleFrame, getBundleLensType, getOpticalParameters, getBundlePackage } from "../../../bundles/state/bundles/selectors.internal.js";
import { getPrescription } from "../../../prescription/state/prescription/selectors.internal.js";
import { getLensTypeSku, getConfiguration, getPackageSku, getAddOnSku } from "./selectors.internal.js";
import { selectLensType, selectPackage, selectAddOn, selectLensTypeSuccess, selectPackageSuccess, selectAddOnSuccess, resetSelectedPackage, resetSelectedAddOn, selectLensTypeFailed, selectPackageFailed, selectAddOnFailed } from "./actions.js";
const filterPrescriptionKeysAndValues = (prescriptionProperties, validOpticalParameters) => {
  const validOpticalParamsMap = validOpticalParameters.reduce(
    (m, p) => ({ ...m, [p.name]: p.values }),
    {}
  );
  return Object.entries(prescriptionProperties).reduce((m, property) => {
    const [pKey, pValue] = property;
    const validPropertyValues = validOpticalParamsMap[pKey];
    if (!validPropertyValues || !pValue) {
      return m;
    }
    if (validPropertyValues.has(pValue)) {
      return {
        ...m,
        [pKey]: pValue
      };
    }
    return m;
  }, {});
};
function* onSelectLensType({
  payload: { frameSku, lensTypeSku, reason }
}) {
  function* fail(error) {
    yield put(
      selectLensTypeFailed({
        frameSku,
        lensTypeSku,
        error
      })
    );
  }
  const bundleFrame = yield select(getBundleFrame, {
    frameSku
  });
  if (!bundleFrame || !bundleFrame.lensTypeSkus.has(lensTypeSku)) {
    return yield* fail(
      new Error(
        `Lens-type with SKU "${lensTypeSku}" can't be selected for frame with SKU "${frameSku}", valid lens-type SKUs are "${bundleFrame && JSON.stringify([...bundleFrame.lensTypeSkus])}"`
      )
    );
  }
  const lensType = yield select(
    getBundleLensType,
    { lensTypeSku }
  );
  if (!lensType || !statusSuccess(lensType)) {
    return yield* fail(new Error("Lens-type not available"));
  }
  const prescription = yield select(
    getPrescription
  );
  const opticalParameters = yield select(
    getOpticalParameters,
    { frameSku, lensTypeSku }
  );
  let prescriptionPrefill = {
    left: {},
    right: {},
    uploadReferences: {}
  };
  if (statusSuccess(opticalParameters)) {
    const validParameters = opticalParameters.result;
    prescriptionPrefill = {
      left: filterPrescriptionKeysAndValues(prescription.left, validParameters),
      right: filterPrescriptionKeysAndValues(prescription.right, validParameters),
      uploadReferences: prescription.uploadReferences
    };
  }
  yield put(
    selectLensTypeSuccess({
      frameSku,
      lensTypeSku,
      prescriptionPrefill,
      reason
    })
  );
}
function* ensureCompatibleLensPackage({
  payload: { frameSku, lensTypeSku }
}) {
  const selectedPackageSku = yield select(
    getPackageSku,
    { frameSku }
  );
  if (!selectedPackageSku) {
    return;
  }
  const lensType = yield select(
    getBundleLensType,
    { lensTypeSku }
  );
  if (!lensType || !statusSuccess(lensType)) {
    return;
  }
  if (!lensType.result.packageSkus.has(selectedPackageSku)) {
    yield put(resetSelectedPackage({ frameSku }));
  }
}
function* ensureCompatibleLensAddOn({
  payload: { frameSku, packageSku }
}) {
  const selectedAddOnSku = yield select(getAddOnSku, {
    frameSku
  });
  if (!selectedAddOnSku) {
    return;
  }
  const bundlePackage = yield select(
    getBundlePackage,
    { packageSku }
  );
  if (!bundlePackage || !statusSuccess(bundlePackage)) {
    return;
  }
  if (!bundlePackage.result.addOnSkus.has(selectedAddOnSku)) {
    yield put(
      resetSelectedAddOn({
        frameSku,
        reason: "default selection"
      })
    );
  }
}
function* onSelectPackage({
  payload: { frameSku, packageSku, reason }
}) {
  function* fail(error) {
    yield put(
      selectPackageFailed({
        frameSku,
        packageSku,
        error
      })
    );
  }
  const lensTypeSku = yield select(getLensTypeSku, {
    frameSku
  });
  if (!lensTypeSku) {
    return yield* fail(new Error(`A lens-type needs to be selected before we can select a package`));
  }
  const lensPackage = yield select(
    getBundlePackage,
    { packageSku }
  );
  if (lensPackage && !statusSuccess(lensPackage)) {
    return yield* fail(new Error("Lens-package is unavailable"));
  }
  const lensType = yield select(
    getBundleLensType,
    { lensTypeSku }
  );
  if (!lensType || !statusSuccess(lensType)) {
    return yield* fail(new Error(`Lens-type with SKU "${lensTypeSku}" is unavailable`));
  }
  if (!lensType.result.packageSkus.has(packageSku)) {
    return yield* fail(
      new Error(`Package is not compatible with selected lens-type with SKU "${lensTypeSku}`)
    );
  }
  yield put(
    selectPackageSuccess({
      frameSku,
      lensTypeSku,
      packageSku,
      reason
    })
  );
}
function* onSelectAddOn({
  payload: { frameSku, addOnSku, reason }
}) {
  function* fail(error) {
    yield put(
      selectAddOnFailed({
        frameSku,
        addOnSku,
        error
      })
    );
  }
  const config = yield select(getConfiguration, {
    frameSku
  });
  if (!config) {
    return yield* fail(
      new Error(
        `No add-on can be selected for Frame: ${frameSku}, there's no active configuration for it`
      )
    );
  }
  const { lensType, package: packageSku } = config;
  if (!lensType?.sku || !packageSku) {
    return yield* fail(
      new Error(
        `To select an add-on we need a lens-type, and a package; current lens-type is "${lensType?.sku}", package is "${packageSku}"`
      )
    );
  }
  const bundlePackage = yield select(
    getBundlePackage,
    { packageSku }
  );
  if (!bundlePackage || !statusSuccess(bundlePackage)) {
    return yield* fail(
      new Error(`Can't select add-on because package with SKU ${packageSku} is unavailable`)
    );
  }
  if (!bundlePackage.result.addOnSkus.has(addOnSku)) {
    return yield* fail(
      new Error(`Add-on cannot be selected for selected package with SKU "${packageSku}"`)
    );
  }
  yield put(
    selectAddOnSuccess({
      frameSku,
      addOnSku,
      reason
    })
  );
}
const selectionHandlers = [
  takeLatest(selectLensType.type, onSelectLensType),
  takeLatest(selectPackage.type, onSelectPackage),
  takeLatest(selectAddOn.type, onSelectAddOn)
];
const configurationSagas = [
  ...selectionHandlers,
  takeLatest(selectLensTypeSuccess.type, ensureCompatibleLensPackage),
  takeLatest(selectPackageSuccess.type, ensureCompatibleLensAddOn)
];
export {
  configurationSagas as default,
  onSelectAddOn,
  onSelectLensType,
  onSelectPackage
};
//# sourceMappingURL=sagas.js.map
